import React, { useState, useEffect, useContext, useCallback, } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';
import MessageDialog from './AlertDialog';

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';
const TEAL_MID = '#4BBBC4';

function TodoDraftUpdateModal({
  open,
  onClose,
  schedule,
  initialFieldTripDate,
  initialDescription,
  status,
  onSubmit,
}) {
  const { authAxios } = useContext(FetchContext);
  const { PRIVATE_ROUTE } = API_ROUTES;

  const [costItems, setCostItems] = useState([]);
  const [selectedDraftId, setSelectedDraftId] = useState('');
  const [noDraftFound, setNoDraftFound] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    severity: 'info',
    title: '',
    message: '',
    okText: 'OK',
    cancelText: null,
    onConfirm: null,
  });

  const [formData, setFormData] = useState({
    costItem: '',
    estimatedHours: '',
    fieldTripDate: null,
    urgency: 'Normal',
    description: '',
  });

  const [errors, setErrors] = useState({});

  const fieldLabelSx = {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#555',
    mb: 0.5,
  };


  const loadTodoDraftData = useCallback(async () => {
    try {
      setLoadingDraft(true);
      setNoDraftFound(false);

      const response = await authAxios.get(
        PRIVATE_ROUTE.GET_TODO_DRAFTS_FOR_SCHEDULE,
        {
          params: {
            job_id: schedule.job_id.number_str,
            task_id: schedule.task._id,
            schedule_id: schedule._id,
          },
        }
      );

      const drafts = response.data?.data || [];

      if (drafts.length === 0) {
        setNoDraftFound(true);
        return;
      }

      setCostItems(drafts);

      const first = drafts[0];

      setSelectedDraftId(first._id);

      setFormData({
        costItem: first.cost_item?.[0] || '',
        estimatedHours: first.estimated_hours,
        fieldTripDate: initialFieldTripDate
          ? dayjs(initialFieldTripDate)
          : null,
        urgency: first.urgency || 'Normal',
        description: initialDescription || first.description || '',
      });

    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDraft(false);
    }
  }, [
    authAxios,
    PRIVATE_ROUTE.GET_TODO_DRAFTS_FOR_SCHEDULE,
    schedule?.job_id?.number_str,
    schedule?.task?._id,
    initialFieldTripDate,
    initialDescription,
    schedule?._id
  ]);

  useEffect(() => {
    if (!open || !schedule?._id) return;

    // loadCostItems();
    setLoadingDraft(true);
    loadTodoDraftData();
  }, [open, schedule?._id, loadTodoDraftData]);

  const handleChange = (field, value) => {
    const updated = {
      ...formData,
      [field]: value,
    };

    if (field === 'costItem') {
      const selected = costItems.find(
        (item) => item.cost_item?.[0] === value
      );

      if (selected) {
        setSelectedDraftId(selected._id);
        updated.estimatedHours = selected.estimated_hours;
        updated.urgency = selected.urgency || 'Normal';
      }
    }

    setFormData(updated);

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.costItem)
      newErrors.costItem = 'Cost Item is required';

    if (
      formData.estimatedHours === '' ||
      formData.estimatedHours === null
    )
      newErrors.estimatedHours =
        'Estimated Hours is required';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const doSave = async () => {
    try {
      const payload = {
        job_id: schedule.job_id.number_str,
        task_id: schedule.task._id,
        schedule_id: schedule._id,
        status,
        cost_item: [formData.costItem],
        estimated_hours: Number(formData.estimatedHours),
        field_trip_date: formData.fieldTripDate
          ? formData.fieldTripDate.format('YYYY-MM-DD')
          : null,
        urgency: formData.urgency || 'Normal',
        description: formData.description,
        skipUpdatedAtCheck: true,
      };

      const response = await authAxios.post(
        `${PRIVATE_ROUTE.EDIT_TODO_DRAFT}${selectedDraftId}`,
        payload
      );

      if (response.data.statusCode === 200) {
        if (onSubmit) {
          await onSubmit(response.data);
        } else {
          onClose();
        }
      } else {
        setMessageDialog({
          open: true,
          severity: 'error',
          title: 'Error',
          message: `Schedule assigned successfully but failed to update the linked Todo Draft. Reason: ${response.data.message}`,
          okText: 'OK',
          cancelText: null,
          onConfirm: null,
        });        
      }
    } catch (err) {
      console.error(err);

      setMessageDialog({
        open: true,
        severity: 'error',
        title: 'Error',
        message: 'Something went wrong while saving.',
        okText: 'OK',
        cancelText: null,
      });
    }
  };

  const confirmTodoDraftSave = async () => {
    // close warning dialog first
    setMessageDialog({
      open: false,
      severity: '',
      title: '',
      message: '',
      okText: 'OK',
      cancelText: null,
      onConfirm: null,
    });

    await doSave();
  };


  const handleSubmit = () => {
    if (!validate()) return;

    const selectedDraft = costItems.find(
      (item) => item._id === selectedDraftId
    );

    if (selectedDraft?.field_trip_date) {
      setMessageDialog({
        open: true,
        severity: 'warning',
        title: 'Warning',
        message:
          'The selected Todo Draft already has a Field Trip Date assigned.\n\nDo you still want to save?',
        okText: 'OK, Save',
        cancelText: 'Cancel',
        onConfirm: confirmTodoDraftSave,
      });
    } else {
      doSave();
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        {loadingDraft ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#fff',
              p: 4,
              borderRadius: 2,
            }}
          >
            <Typography fontWeight={600}>
              Checking Todo Draft...
            </Typography>
          </Box>
        ) : noDraftFound ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 480 },
              bgcolor: '#fff',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 24,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
                px: 3,
                py: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AssignmentTurnedInIcon sx={{ color: '#fff' }} />

                <Typography
                  variant="h6"
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  Todo Draft Not Found
                </Typography>
              </Box>

              <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ px: 4, py: 4, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#333',
                  mb: 3,
                }}
              >
                No Todo Draft Record Found
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    bgcolor: TEAL_LIGHT,
                    color: TEAL_DARK,
                    px: 2,
                    py: 0.75,
                    borderRadius: 5,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                  }}
                >
                  Job #{schedule?.job_id?.number_str}
                </Box>

                <Box
                  sx={{
                    bgcolor: TEAL_LIGHT,
                    color: TEAL_DARK,
                    px: 2,
                    py: 0.75,
                    borderRadius: 5,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                  }}
                >
                  Task #{schedule?.task?.number_str}
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderTop: `1px solid ${TEAL_LIGHT}`,
                display: 'flex',
                justifyContent: 'flex-end',
                bgcolor: '#fafafa',
              }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: TEAL_DARK,
                  minWidth: 100,
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: TEAL,
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95vw', sm: 650 },
              bgcolor: '#fff',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AssignmentTurnedInIcon sx={{ color: '#fff' }} />

                <Box>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '0.68rem',
                      letterSpacing: 1,
                      fontWeight: 700,
                    }}
                  >
                    TODO DRAFT
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    Update Todo Draft
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mt: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#ffffff20',
                        borderRadius: 20,
                        px: 1.2,
                        py: 0.3,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                        }}
                      >
                        JOB&nbsp;&nbsp;{schedule.job_id?.number_str}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#ffffff20',
                        borderRadius: 20,
                        px: 1.2,
                        py: 0.3,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                        }}
                      >
                        TASK&nbsp;&nbsp;{schedule.task?.number_str}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: '#fff' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Body */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: 3,
                py: 3,
              }}
            >
              <Grid container spacing={2}>

                {/* Cost Item */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      ...fieldLabelSx,
                      color: errors.costItem ? '#d32f2f' : '#555',
                    }}
                  >
                    Cost Item
                  </Typography>

                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.costItem}
                  >
                    <Select
                      value={selectedDraftId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedDraftId(id);

                        const selected = costItems.find(item => item._id === id);

                        if (selected) {
                          setFormData(prev => ({
                            ...prev,
                            costItem: selected.cost_item?.[0] || '',
                            estimatedHours: selected.estimated_hours,
                            urgency: selected.urgency || 'Normal',
                          }));
                        }
                      }}
                    >
                      {costItems.map((item) => (
                        <MenuItem
                          key={item._id}
                          value={item._id}
                        >
                          {item.cost_item[0]} ({item.estimated_hours} hrs)
                        </MenuItem>
                      ))}
                    </Select>

                    {errors.costItem && (
                      <FormHelperText>
                        {errors.costItem}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Estimated Hours */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      ...fieldLabelSx,
                      color: errors.estimatedHours
                        ? '#d32f2f'
                        : '#555',
                    }}
                  >
                    Estimated Hours
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={formData.estimatedHours}
                    onChange={(e) =>
                      handleChange(
                        'estimatedHours',
                        e.target.value
                      )
                    }
                    error={!!errors.estimatedHours}
                    helperText={errors.estimatedHours}
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: TEAL,
                      },
                    }}
                  />
                </Grid>

                {/* Field Trip Date */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      ...fieldLabelSx,
                      color: errors.fieldTripDate
                        ? '#d32f2f'
                        : '#555',
                    }}
                  >
                    Field Trip Date
                  </Typography>

                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                  >
                    <DatePicker
                      value={formData.fieldTripDate}
                      onChange={(value) => handleChange('fieldTripDate', value)}
                      disabled={!formData.fieldTripDate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          error={!!errors.fieldTripDate}
                          helperText={errors.fieldTripDate}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      ...fieldLabelSx,
                      color: errors.urgency ? '#d32f2f' : '#555',
                    }}
                  >
                    Urgency
                  </Typography>

                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.urgency}
                  >
                    <Select
                      value={formData.urgency}
                      onChange={(e) => handleChange('urgency', e.target.value)}
                    >
                      <MenuItem value="Normal">Normal</MenuItem>
                      <MenuItem value="Urgent">Urgent</MenuItem>
                    </Select>

                    {errors.urgency && (
                      <FormHelperText>{errors.urgency}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      ...fieldLabelSx,
                      color: errors.description ? '#d32f2f' : '#555',
                    }}
                  >
                    Description
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={formData.description}
                    onChange={(e) =>
                      handleChange('description', e.target.value)
                    }
                    error={!!errors.description}
                    helperText={errors.description}
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: TEAL,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderTop: `1px solid ${TEAL_LIGHT}`,
                background: '#fafafa',
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderColor: TEAL_MID,
                    color: TEAL_DARK,
                    '&:hover': {
                      borderColor: TEAL_DARK,
                      background: TEAL_LIGHT,
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    background: TEAL_DARK,
                    px: 4,
                    fontWeight: 700,
                    '&:hover': {
                      background: TEAL,
                    },
                  }}
                >
                  Submit
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Modal>
      <MessageDialog
        open={messageDialog.open}
        severity={messageDialog.severity}
        title={messageDialog.title}
        message={messageDialog.message}
        okText={messageDialog.okText}
        cancelText={messageDialog.cancelText}
        onClose={() =>
          setMessageDialog((prev) => ({
            ...prev,
            open: false,
          }))
        }
        onConfirm={messageDialog.onConfirm}
      />
    </>
  );
}

TodoDraftUpdateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  status: PropTypes.string,
  schedule: PropTypes.shape({
    _id: PropTypes.string,
    updatedAt: PropTypes.string,
    job_id: PropTypes.shape({
      number_str: PropTypes.string,
    }),
    task: PropTypes.shape({
      _id: PropTypes.string,
      number_str: PropTypes.string,
    }),
  }).isRequired,

  initialCostItem: PropTypes.string,
  initialEstimatedHours: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  initialFieldTripDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  initialDescription: PropTypes.string,
};

TodoDraftUpdateModal.defaultProps = {
  initialCostItem: '',
  initialEstimatedHours: '',
  initialFieldTripDate: null,
  status: 'ready for drafting',
  initialDescription: '',
  onSubmit: null,
};

export default TodoDraftUpdateModal;